/**
 * @file Recalls notes/history modal and tagged-note save path.
 */

import {
    collection,
    doc,
    getDoc,
    onSnapshot,
    orderBy,
    query,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";
import { db } from "../../app/firebase/firebase.js";
import {
    saveTextFieldAndTagUsers,
} from "../../app/firebase/callables.js";
import { appData, state } from "../../app/state/state.js";
import {
    AUTO_GROWING_TEXTAREA_CLASSES,
    bindNativeFieldBehavior,
} from "../../shared/forms/nativeFieldStyles.js";
import {
    closeTaggingDropdown,
    initTagging,
} from "../../shared/ui/tagging.js";
import {
    lockPageScroll,
    unlockPageScroll,
} from "../../shared/ui/pageScrollLock.js";
import {
    escapeHTML,
    formatAndSanitizeUserContent,
    formatContentForSave,
    sanitizeHTML,
} from "../../shared/utils/contentUtils.js";
import {
    formatDate,
} from "../../shared/utils/dateUtils.js";
import { formatRecallsSystemNoteContent } from "./recallsSystemNoteContent.js";

const MODAL_ID = "recalls-notes-history-modal";
const IMPORTED_SYSTEM_NOTE_TYPE = "recalls_imported";
const NOTES_MODAL_SCROLL_LOCK = Symbol("recalls-notes-scroll-lock");

let activeNotesUnsubscribe = null;
let activeRecall = null;
let activeNotes = [];
let activePendingNotes = [];

function attrValue(value) {
    return escapeHTML(String(value ?? ""))
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function getRecallDisplayName(recall = {}) {
    return [recall.first_name, recall.last_name].filter(Boolean).join(" ") ||
        recall.email ||
        "Recall";
}

function getCachedRecall(recallId) {
    return [
        ...(appData.recalls || []),
        ...(appData.closedRecalls || []),
    ].find((item) => item.id === recallId) || null;
}

async function resolveRecall(recallOrId) {
    if (typeof recallOrId === "object" && recallOrId?.id) {
        return recallOrId;
    }

    const recallId = String(recallOrId || "");
    const cached = getCachedRecall(recallId);
    if (cached) {return cached;}

    const snapshot = await getDoc(doc(db, "recalls", recallId));
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}

function getUserDisplayName(userId, fallback = "Unknown User") {
    if (userId === "system") {return "System";}
    const user = (appData.users || []).find((entry) => entry.id === userId);
    return user?.name || user?.email || fallback;
}

function getCurrentUserDisplayName() {
    return state.currentUserProfile?.name ||
        state.currentUserProfile?.email ||
        state.currentUser?.displayName ||
        state.currentUser?.email ||
        "Unknown User";
}

function getCurrentUserId() {
    return state.currentUser?.uid ||
        state.currentUserProfile?.id ||
        state.currentUserProfile?.uid ||
        "";
}

function getDateValue(value) {
    if (!value) {return null;}
    if (value?.toDate) {return value.toDate();}
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

function getNoteCreatedAtMs(note = {}) {
    return getDateValue(note.createdAt)?.getTime?.() ||
        Number(note.createdAtMs || 0) ||
        0;
}

function formatDateTimeAmPm(value) {
    const date = getDateValue(value);
    if (!date) {return "";}
    const dateLabel = formatDate(date);
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const period = hours >= 12 ? "pm" : "am";
    const displayHour = hours % 12 || 12;
    return `${dateLabel} ${displayHour}:${minutes} ${period}`;
}

function isImportedSystemNote(note = {}) {
    return note.isSystemNote === true &&
        note.systemNote?.type === IMPORTED_SYSTEM_NOTE_TYPE;
}

function isImportedUserNote(note = {}) {
    return note.importedNote === true;
}

function getNoteDateLabel(note = {}) {
    const date =
        note.createdAt ||
        note.createdAtMs ||
        null;
    return formatDateTimeAmPm(date);
}

function formatSystemNoteTimestampText(content) {
    return formatRecallsSystemNoteContent(content).replace(
        /\bon (\d{2}\/\d{2}\/\d{4}) at ([01]?\d|2[0-3]):([0-5]\d)\b(?!\s*(?:am|pm)\b)/gi,
        (_match, date, hourValue, minute) => {
            const hour = Number(hourValue);
            const period = hour >= 12 ? "pm" : "am";
            const displayHour = hour % 12 || 12;
            return `on ${date} at ${displayHour}:${minute} ${period}`;
        },
    );
}

function getImportedSystemNoteContent(actorName = "Unknown User") {
    return `Imported by ${actorName}`;
}

function getImportNoteGroupKey(note = {}) {
    const metadata = isImportedSystemNote(note) ?
        note.systemNote?.metadata :
        note.importMetadata;
    const importRunId = String(metadata?.importRunId || "").trim();
    const sourceRowNumber = String(metadata?.sourceRowNumber || "").trim();
    if (!importRunId && !sourceRowNumber) {return "";}
    return `${importRunId}:${sourceRowNumber}`;
}

function getImportNoteRank(note = {}) {
    if (isImportedUserNote(note)) {return 0;}
    if (isImportedSystemNote(note)) {return 1;}
    return 2;
}

function compareRecallNotes(noteA = {}, noteB = {}) {
    const groupA = getImportNoteGroupKey(noteA);
    const groupB = getImportNoteGroupKey(noteB);
    if (groupA && groupA === groupB) {
        const rankDiff = getImportNoteRank(noteA) - getImportNoteRank(noteB);
        if (rankDiff !== 0) {return rankDiff;}
    }

    const timeDiff = getNoteCreatedAtMs(noteB) - getNoteCreatedAtMs(noteA);
    if (timeDiff !== 0) {return timeDiff;}

    return String(noteA.id || "").localeCompare(String(noteB.id || ""));
}

/**
 * Renders one Recalls note/history card.
 * @param {object} note Note document data.
 * @returns {string} Note card HTML.
 */
export function renderRecallNoteHistoryItem(note = {}) {
    const pending = note.isPending === true;
    const isSystemNote = note.isSystemNote === true;
    const actorName = isSystemNote ?
        getUserDisplayName(note.systemNote?.actorUserId || note.createdBy) :
        (pending ? getCurrentUserDisplayName() : getUserDisplayName(note.createdBy));
    const noteDate = getNoteDateLabel(note);
    const userNoteAttribution = note.importedNote === true ?
        "Imported by" :
        "By";
    const content = isImportedSystemNote(note) ?
        getImportedSystemNoteContent(actorName) :
        isSystemNote ?
        formatSystemNoteTimestampText(note.content) :
        note.content;
    const contentHtml = formatAndSanitizeUserContent(content);
    if (isSystemNote) {
        return `
            <article class="rounded-md border border-gray-200 bg-gray-50/80 p-2.5 text-gray-500 dark:border-gray-600 dark:bg-gray-700/60 dark:text-gray-400">
                <div class="text-sm italic text-gray-400 dark:text-gray-500">${contentHtml}</div>
                <p class="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
                    System note generated by ${sanitizeHTML(actorName)}
                    ${noteDate ? ` on ${sanitizeHTML(noteDate)}` : ""}
                </p>
            </article>
        `;
    }

    return `
        <article class="rounded-md border ${pending ? "border-[var(--dfc-brand-primary)]/60" : "border-gray-200"} bg-gray-50 p-2.5 dark:border-gray-600 dark:bg-gray-700" ${pending ? `data-recalls-pending-note="${attrValue(note.id)}"` : ""}>
            <div class="text-sm text-gray-800 dark:text-gray-300">${contentHtml}</div>
            <p class="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
                ${userNoteAttribution} ${sanitizeHTML(actorName)}
                ${noteDate ? ` on ${sanitizeHTML(noteDate)}` : ""}
                ${pending ? " - Saving..." : ""}
            </p>
        </article>
    `;
}

function renderNotesList() {
    const notes = [...activePendingNotes, ...activeNotes];
    if (!notes.length) {
        return `
            <div class="rounded-md border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                No notes yet.
            </div>
        `;
    }

    return notes.map(renderRecallNoteHistoryItem).join("");
}

function ensureModal() {
    let modal = document.getElementById(MODAL_ID);
    if (modal) {return modal;}

    modal = document.createElement("div");
    modal.id = MODAL_ID;
    modal.className = "fixed inset-0 z-50 hidden items-center justify-center bg-black/40 p-4";
    modal.innerHTML = `
        <div class="flex max-h-[calc(100vh-2rem)] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl">
            <div class="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
                <div class="min-w-0">
                    <h2 class="truncate text-lg font-semibold text-gray-900" data-recalls-notes-title>Recall Notes</h2>
                    <p class="mt-1 text-sm text-gray-500" data-recalls-notes-subtitle></p>
                </div>
                <button type="button" class="recalls-notes-close standard-action-icon-btn standard-action-icon-btn--danger shrink-0" aria-label="Close notes">
                    <span class="material-symbols-outlined text-[20px]">close</span>
                </button>
            </div>
            <div class="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                <form class="mb-4" data-recalls-note-form>
                    <textarea
                        name="note"
                        rows="3"
                        class="${AUTO_GROWING_TEXTAREA_CLASSES} min-h-[82px] focus:hover:border-gray-200 sm:min-h-[68px]"
                        placeholder="+ Add a new note"
                        data-auto-grow="true"
                    ></textarea>
                    <div class="mt-2 flex items-center justify-between gap-3">
                        <p class="text-xs text-gray-500" data-recalls-note-message></p>
                        <button type="submit" class="inline-flex h-9 min-w-[92px] items-center justify-center rounded-md bg-[var(--dfc-brand-primary)] px-4 text-sm font-semibold text-[var(--dfc-brand-primary-contrast)] hover:bg-[var(--dfc-brand-primary-hover)] disabled:cursor-not-allowed disabled:opacity-50">
                            Save Note
                        </button>
                    </div>
                </form>
                <div class="space-y-3" data-recalls-notes-list></div>
            </div>
        </div>
    `;
    document.body.append(modal);
    return modal;
}

/**
 * Closes the Recalls notes/history modal and detaches its listener.
 * @returns {void}
 */
export function closeRecallNotesHistoryModal() {
    if (activeNotesUnsubscribe) {
        activeNotesUnsubscribe();
        activeNotesUnsubscribe = null;
    }
    activeRecall = null;
    activeNotes = [];
    activePendingNotes = [];
    const modal = document.getElementById(MODAL_ID);
    const form = modal?.querySelector("[data-recalls-note-form]");
    const textarea = form?.querySelector("textarea[name=\"note\"]");
    const message = modal?.querySelector("[data-recalls-note-message]");
    if (textarea) {
        textarea.value = "";
    }
    if (message) {
        message.textContent = "";
    }
    closeTaggingDropdown();
    modal?.classList.add("hidden");
    modal?.classList.remove("flex");
    unlockPageScroll(NOTES_MODAL_SCROLL_LOCK);
}

function createPendingNote(content) {
    return {
        id: `pending-note-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        content,
        createdAtMs: Date.now(),
        createdBy: getCurrentUserId(),
        isPending: true,
    };
}

function reconcilePendingNotes(savedNotes) {
    if (!activePendingNotes.length || !Array.isArray(savedNotes)) {return;}

    activePendingNotes = activePendingNotes.filter((pendingNote) => {
        const hasSavedMatch = savedNotes.some((savedNote) => {
            if (savedNote.isSystemNote === true) {return false;}
            if (String(savedNote.content || "").trim() !== pendingNote.content) {
                return false;
            }
            const savedAtMs =
                savedNote.createdAt?.toMillis?.() ||
                savedNote.createdAt?.toDate?.()?.getTime?.() ||
                0;
            return savedAtMs === 0 ||
                Math.abs(savedAtMs - pendingNote.createdAtMs) <= 120000;
        });
        return !hasSavedMatch;
    });
}

function renderModal() {
    const modal = ensureModal();
    modal.querySelector("[data-recalls-notes-title]").textContent =
        `${getRecallDisplayName(activeRecall)} Notes`;
    modal.querySelector("[data-recalls-notes-subtitle]").textContent =
        activeRecall?.recall_date ?
            `Recall date: ${formatDate(activeRecall.recall_date)}` :
            "";
    modal.querySelector("[data-recalls-notes-list]").innerHTML = renderNotesList();
}

function bindModal(modal) {
    modal.querySelectorAll(".recalls-notes-close").forEach((button) => {
        button.onclick = closeRecallNotesHistoryModal;
    });
    modal.onclick = null;

    const form = modal.querySelector("[data-recalls-note-form]");
    const message = modal.querySelector("[data-recalls-note-message]");
    let textarea = form?.querySelector("textarea[name=\"note\"]");
    if (textarea && activeRecall?.id) {
        textarea.value = "";
        initTagging(textarea, activeRecall.id, "recalls");
        textarea = form.querySelector("textarea[name=\"note\"]");
    }
    if (message) {
        message.textContent = "";
    }
    bindNativeFieldBehavior(form);

    form.onsubmit = async (event) => {
        event.preventDefault();
        const rawText = textarea?.value.trim() || "";
        if (!rawText || !activeRecall?.id) {
            message.textContent = "Enter a note before saving.";
            return;
        }

        const savedContent = formatContentForSave(rawText);
        const pendingNote = createPendingNote(savedContent);
        activePendingNotes.unshift(pendingNote);
        textarea.value = "";
        textarea.dispatchEvent(new Event("input", { bubbles: true }));
        message.textContent = "";
        renderModal();
        try {
            await saveTextFieldAndTagUsers({
                content: savedContent,
                parentId: activeRecall.id,
                parentType: "recalls",
                saveMode: "notesSubcollection",
            });
            message.textContent = "";
        } catch (error) {
            console.error("Failed to save Recall note:", error);
            activePendingNotes = activePendingNotes.filter((note) =>
                note.id !== pendingNote.id);
            renderModal();
            textarea.value = rawText;
            textarea.dispatchEvent(new Event("input", { bubbles: true }));
            message.textContent = error?.message || "Could not save this note.";
        }
    };
}

function attachNotesListener(recallId) {
    if (activeNotesUnsubscribe) {
        activeNotesUnsubscribe();
        activeNotesUnsubscribe = null;
    }
    activeNotesUnsubscribe = onSnapshot(
        query(
            collection(db, "recalls", recallId, "notes"),
            orderBy("createdAt", "desc"),
        ),
        (snapshot) => {
            const savedNotes = snapshot.docs.map((noteDoc) => ({
                id: noteDoc.id,
                ...noteDoc.data(),
            }));
            reconcilePendingNotes(savedNotes);
            activeNotes = savedNotes.sort(compareRecallNotes);
            renderModal();
        },
        (error) => {
            console.error("Failed to listen to Recall notes:", error);
            activeNotes = [];
            renderModal();
        },
    );
}

/**
 * Opens the Recalls notes/history modal.
 * @param {object|string} recallOrId Recall row or id.
 * @returns {Promise<void>}
 */
export async function openRecallNotesHistoryModal(recallOrId) {
    let recall;
    try {
        recall = await resolveRecall(recallOrId);
    } catch (error) {
        console.error("Failed to resolve Recall for notes modal:", error);
        return;
    }
    if (!recall?.id) {return;}

    activeRecall = recall;
    activeNotes = [];
    activePendingNotes = [];
    const modal = ensureModal();
    renderModal();
    bindModal(modal);
    attachNotesListener(recall.id);
    modal.classList.remove("hidden");
    modal.classList.add("flex");
    lockPageScroll(NOTES_MODAL_SCROLL_LOCK);
}

/**
 * Binds table buttons that open the Recalls notes/history modal.
 * @param {HTMLElement} container Recalls route container.
 * @returns {void}
 */
export function bindRecallsNotesActions(container) {
    container.querySelectorAll("[data-recalls-open-notes]").forEach((button) => {
        button.addEventListener("click", () => {
            openRecallNotesHistoryModal(button.dataset.recallsOpenNotes);
        });
    });
}
